// Maps low-level upload errors (Supabase Storage, network) into an actionable
// message the user can act on. Pure & testable.

export type UploadErrorReason =
  | "too_large"
  | "unsupported_type"
  | "unauthorized"
  | "network"
  | "quota"
  | "duplicate"
  | "unknown";

export type UploadErrorInfo = {
  reason: UploadErrorReason;
  title: string;
  message: string;
  retryable: boolean;
};

export const MAX_VISION_UPLOAD_BYTES = 50 * 1024 * 1024; // 50 MB

const truthy = (v: unknown): v is string => typeof v === "string" && v.length > 0;

export function describeUploadError(err: unknown, file?: { size?: number; type?: string }): UploadErrorInfo {
  // File-side checks first — surface a clear "do this" message rather than
  // letting the request fail server-side.
  if (file?.size && file.size > MAX_VISION_UPLOAD_BYTES) {
    return {
      reason: "too_large",
      title: "File too large",
      message: `Files must be under ${Math.round(MAX_VISION_UPLOAD_BYTES / (1024 * 1024))} MB. Try compressing or trimming it first.`,
      retryable: false,
    };
  }

  const raw =
    (err && typeof err === "object" && (
      (err as { message?: string }).message ||
      (err as { error?: string }).error ||
      (err as { statusText?: string }).statusText
    )) || (typeof err === "string" ? err : "");
  const msg = (truthy(raw) ? raw : "").toLowerCase();
  const status = err && typeof err === "object" ? (err as { status?: number; statusCode?: number }).status ?? (err as { statusCode?: number }).statusCode : undefined;

  if (status === 413 || msg.includes("payload too large") || msg.includes("exceeded the maximum")) {
    return {
      reason: "too_large",
      title: "File too large",
      message: "The file exceeded the server limit. Try a smaller version.",
      retryable: false,
    };
  }
  if (status === 401 || status === 403 || msg.includes("unauthorized") || msg.includes("not authorized") || msg.includes("jwt")) {
    return {
      reason: "unauthorized",
      title: "Sign-in expired",
      message: "Your session expired. Please refresh and sign in again to upload.",
      retryable: false,
    };
  }
  if (status === 409 || msg.includes("duplicate") || msg.includes("already exists")) {
    return {
      reason: "duplicate",
      title: "Already uploaded",
      message: "That file already exists in your gallery — retrying will create a fresh copy.",
      retryable: true,
    };
  }
  if (msg.includes("invalid_mime") || msg.includes("mime") || msg.includes("unsupported")) {
    return {
      reason: "unsupported_type",
      title: "Unsupported file type",
      message: "Pick an image (PNG/JPG/WEBP), video (MP4/WEBM), or voice note (M4A/MP3/WEBM).",
      retryable: false,
    };
  }
  if (msg.includes("quota") || msg.includes("storage limit")) {
    return {
      reason: "quota",
      title: "Storage full",
      message: "Your vision board storage is full. Delete an item and try again.",
      retryable: false,
    };
  }
  if (msg.includes("network") || msg.includes("fetch") || msg.includes("failed to fetch") || msg.includes("timeout")) {
    return {
      reason: "network",
      title: "Connection problem",
      message: "We couldn't reach storage. Check your connection and tap retry.",
      retryable: true,
    };
  }
  return {
    reason: "unknown",
    title: "Upload failed",
    message: "Something went wrong uploading your file. Tap retry to try again.",
    retryable: true,
  };
}
